import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import moment from 'moment/min/moment-with-locales';
import { ThemeProvider } from '@mui/material/styles';
import { Alert, Box, Chip, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { api } from 'common';
import { formatCop as formatCopRaw } from 'common/src/other/currencyFormat';
import CircularLoading from '../components/CircularLoading';
import { C2PrimaryButton, C2SecondaryButton } from '../components/C2Dialog';
import { C2, C2_FONT, c2TableTheme, C2TableCard, C2SectionHeading, C2StatusChip, C2Switch } from '../components/Table/C2UserTable';

const formatCop = (v) => formatCopRaw(Math.round(Number(v) || 0));
const digits = (v, max = 10) => String(v ?? '').replace(/\D/g, '').slice(0, max);
const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const STATUS_UI = {
  active: { tone: 'success', label: 'Activo' },
  hidden: { tone: 'neutral', label: 'Oculto' },
  expired: { tone: 'danger', label: 'Vencido' },
  exhausted: { tone: 'warning', label: 'Agotado' },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontFamily: C2_FONT,
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: C2.border },
    '&:hover fieldset': { borderColor: C2.bright },
    '&.Mui-focused fieldset': { borderColor: C2.violet },
  },
  '& .MuiInputLabel-root': { fontFamily: C2_FONT, color: C2.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: C2.violet },
  '& .MuiFormHelperText-root': { fontFamily: C2_FONT },
};

const pillSx = (active) => ({
  fontFamily: C2_FONT,
  fontWeight: 700,
  borderRadius: '10px',
  px: 1,
  border: `1px solid ${active ? C2.violet : C2.border}`,
  color: active ? C2.white : C2.violet,
  background: active ? `linear-gradient(90deg, ${C2.violet} 0%, ${C2.bright} 100%)` : C2.lavender,
  '&:hover': { background: active ? `linear-gradient(90deg, ${C2.violet} 0%, ${C2.bright} 100%)` : C2.border },
});

const randomCode = () => Array.from({ length: 8 }, () => CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]).join('');

const emptyForm = () => ({
  promo_name: '',
  promo_description: '',
  promo_code: '',
  promo_discount_type: 'percentage',
  promo_discount_value: '',
  max_promo_discount_value: '',
  min_order: '',
  promo_validity: dayjs().add(30, 'day').endOf('day').valueOf(),
  promo_usage_limit: '',
  promo_show: true,
});

const toForm = (p) => ({
  promo_name: p.promo_name || '',
  promo_description: p.promo_description || '',
  promo_code: p.promo_code || '',
  promo_discount_type: p.promo_discount_type === 'flat' ? 'flat' : 'percentage',
  promo_discount_value: p.promo_discount_value ? String(p.promo_discount_value) : '',
  max_promo_discount_value: p.max_promo_discount_value ? String(p.max_promo_discount_value) : '',
  min_order: p.min_order ? String(p.min_order) : '',
  promo_validity: Number(p.promo_validity) || null,
  promo_usage_limit: p.promo_usage_limit ? String(p.promo_usage_limit) : '',
  promo_show: !!p.promo_show,
});

function validate(form, { otherCodes, usedCount, originalValidity }) {
  const e = {};
  const code = api.normalizePromoCode(form.promo_code);
  const value = Number(form.promo_discount_value) || 0;
  const cap = Number(form.max_promo_discount_value) || 0;
  const minOrder = Number(form.min_order) || 0;
  const limit = Number(form.promo_usage_limit) || 0;
  const validity = Number(form.promo_validity) || 0;

  if (!form.promo_name.trim()) e.promo_name = 'Escribe el nombre de la promoción.';
  if (!form.promo_description.trim()) e.promo_description = 'Escribe una descripción corta.';
  if (!code) e.promo_code = 'Escribe o genera un código.';
  else if (!/^[A-Z0-9]{3,20}$/.test(code)) e.promo_code = 'Usa de 3 a 20 letras o números, sin espacios ni símbolos.';
  else if (otherCodes.has(code)) e.promo_code = 'Ya existe otra promoción con este código.';

  if (form.promo_discount_type === 'percentage') {
    if (value < 1 || value > 100) e.promo_discount_value = 'El porcentaje debe estar entre 1 y 100.';
    if (cap <= 0) e.max_promo_discount_value = 'Define el descuento máximo en pesos.';
                    } else {
    if (value <= 0) e.promo_discount_value = 'Define el valor del descuento.';
    else if (minOrder < value) e.min_order = 'El pedido mínimo debe ser igual o mayor que el descuento fijo.';
  }

  if (!validity) e.promo_validity = 'Elige la fecha de fin.';
  else if (validity <= Date.now() && validity !== originalValidity) e.promo_validity = 'La fecha de fin debe ser futura.';

  if (limit > 0 && limit < usedCount) e.promo_usage_limit = `Ya se registraron ${usedCount} usos; el límite no puede ser menor.`;
  return e;
}

function PreviewRow({ label, value, accent, strong }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 0.9, borderTop: '1px solid rgba(235,231,251,0.14)' }}>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.84rem', color: 'rgba(251,250,254,0.72)' }}>{label}</Typography>
      <Typography sx={{ fontFamily: C2_FONT, fontWeight: strong ? 800 : 700, fontSize: strong ? '1.2rem' : '0.92rem', color: accent || C2.white, textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function EditPromo() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const settings = useSelector((s) => s.settingsdata.settings);
  const promodata = useSelector((s) => s.promodata);
  const langLocale = useSelector((s) => s.auth.profile?.lang?.langLocale) || null;

  const original = useMemo(() => (id ? (promodata.promos || []).find((p) => p.id === id) || null : null), [id, promodata.promos]);
  const [form, setForm] = useState(null);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [sampleFare, setSampleFare] = useState('');

  useEffect(() => {
    if (form) return;
    if (!id) setForm(emptyForm());
    else if (original) setForm(toForm(original));
    else if (!promodata.loading) navigate('/404');
  }, [id, original, promodata.loading, form, navigate]);

  const goBack = () => navigate('/promos', { state: { pageNo: state?.pageNo } });

  const otherCodes = useMemo(
    () => new Set((promodata.promos || []).filter((p) => p.id !== id).map((p) => api.normalizePromoCode(p.promo_code))),
    [promodata.promos, id]
  );
  const usedCount = Number(original?.user_avail) || 0;
  const errors = useMemo(
    () => (form ? validate(form, { otherCodes, usedCount, originalValidity: Number(original?.promo_validity) || null }) : {}),
    [form, otherCodes, usedCount, original]
  );
  const hasErrors = Object.keys(errors).length > 0;

  if (!form) return <CircularLoading />;

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const err = (key) => (touched ? errors[key] : undefined);
  const isPct = form.promo_discount_type === 'percentage';
  const code = api.normalizePromoCode(form.promo_code);

  const promoPreview = {
    ...form,
    promo_code: code,
    promo_discount_value: Number(form.promo_discount_value) || 0,
    max_promo_discount_value: isPct ? Number(form.max_promo_discount_value) || 0 : Number(form.promo_discount_value) || 0,
    min_order: Number(form.min_order) || 0,
    promo_usage_limit: Number(form.promo_usage_limit) || 0,
    user_avail: usedCount,
  };
  const status = api.promoStatus(promoPreview);
  const fare = Number(sampleFare) || Math.max(promoPreview.min_order, 20000);
  const eligible = fare >= promoPreview.min_order;
  const discount = eligible ? api.computePromoDiscount(promoPreview, fare, settings?.decimal) : 0;
  const discountText = isPct ? `${promoPreview.promo_discount_value || 0}% OFF` : `${formatCop(promoPreview.promo_discount_value)} OFF`;

  const handleSave = async () => {
    setTouched(true);
    if (hasErrors) return;
    if (!settings.AllowCriticalEditsAdmin) {
      setNotice({ severity: 'warning', msg: t('demo_mode') });
      return;
    }
    const payload = {
      id,
      promo_name: form.promo_name.trim(),
      promo_description: form.promo_description.trim(),
      promo_code: code,
      promo_discount_type: form.promo_discount_type,
      promo_discount_value: promoPreview.promo_discount_value,
      max_promo_discount_value: promoPreview.max_promo_discount_value,
      min_order: promoPreview.min_order,
      promo_validity: Number(form.promo_validity),
      promo_usage_limit: promoPreview.promo_usage_limit,
      promo_show: !!form.promo_show,
      createdAt: Number(original?.createdAt) || Date.now(),
    };
    setSaving(true);
    try {
      await dispatch(api.editPromo(payload, id ? 'Update' : 'Add'));
      [payload.promo_name, payload.promo_description].forEach((text) =>
        Promise.resolve(api.convertLanguage(text, langLocale)).catch(() => null)
      );
      goBack();
    } catch (e) {
      setSaving(false);
      setNotice({ severity: 'error', msg: 'No se pudo guardar la promoción. Revisa tu conexión e inténtalo de nuevo.' });
    }
  };

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <ThemeProvider theme={c2TableTheme}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <C2SecondaryButton startIcon={<ArrowBackRoundedIcon />} onClick={goBack}>
            Volver a promociones
          </C2SecondaryButton>
          {id ? <C2StatusChip tone={STATUS_UI[status].tone} label={STATUS_UI[status].label} /> : null}
        </Box>

        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
            {notice.msg}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <C2TableCard sx={{ mb: 2 }}>
              <C2SectionHeading title={id ? 'Editar promoción' : 'Nueva promoción'} subtitle="Así la verán los usuarios al elegir un cupón en la app." />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                        <TextField
                            fullWidth
                    label="Nombre"
                    placeholder="Ej.: Bienvenida Conecta2"
                    value={form.promo_name}
                    onChange={(e) => set('promo_name')(e.target.value.slice(0, 60))}
                    error={!!err('promo_name')}
                    helperText={err('promo_name')}
                    sx={fieldSx}
                        />
                    </Grid>
                <Grid item xs={12} sm={5}>
                        <TextField
                            fullWidth
                    label="Código"
                    placeholder="BIENVENIDA"
                    value={form.promo_code}
                    onChange={(e) => set('promo_code')(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20))}
                    error={!!err('promo_code')}
                    helperText={err('promo_code') || 'Letras y números, sin espacios.'}
                    sx={{ ...fieldSx, '& input': { fontWeight: 800, letterSpacing: '0.08em' } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Chip size="small" icon={<AutorenewRoundedIcon />} label="Generar" onClick={() => set('promo_code')(randomCode())} sx={pillSx(false)} />
                        </InputAdornment>
                      ),
                    }}
                        />
                    </Grid>
                <Grid item xs={12}>
                        <TextField
                            fullWidth
                    label="Descripción"
                    placeholder="Ej.: 20% en tu primer viaje, hasta $5.000"
                    value={form.promo_description}
                    onChange={(e) => set('promo_description')(e.target.value.slice(0, 140))}
                    error={!!err('promo_description')}
                    helperText={err('promo_description') || `${form.promo_description.length}/140`}
                    sx={fieldSx}
                        />
                    </Grid>
              </Grid>
            </C2TableCard>

            <C2TableCard sx={{ mb: 2 }}>
              <C2SectionHeading title="Descuento" subtitle="El descuento nunca supera el valor del viaje." />
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label="Porcentaje" onClick={() => set('promo_discount_type')('percentage')} sx={pillSx(isPct)} />
                <Chip label="Valor fijo" onClick={() => set('promo_discount_type')('flat')} sx={pillSx(!isPct)} />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={isPct ? 4 : 6}>
                        <TextField
                            fullWidth
                    label={isPct ? 'Porcentaje' : 'Valor del descuento (COP)'}
                    value={form.promo_discount_value}
                    onChange={(e) => set('promo_discount_value')(digits(e.target.value, isPct ? 3 : 9))}
                    inputProps={{ inputMode: 'numeric' }}
                    InputProps={isPct ? { endAdornment: <InputAdornment position="end">%</InputAdornment> } : undefined}
                    error={!!err('promo_discount_value')}
                    helperText={err('promo_discount_value') || (!isPct && Number(form.promo_discount_value) > 0 ? formatCop(form.promo_discount_value) : ' ')}
                    sx={fieldSx}
                        />
                    </Grid>
                {isPct ? (
                  <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                      label="Descuento máximo (COP)"
                      value={form.max_promo_discount_value}
                      onChange={(e) => set('max_promo_discount_value')(digits(e.target.value, 9))}
                      inputProps={{ inputMode: 'numeric' }}
                      error={!!err('max_promo_discount_value')}
                      helperText={err('max_promo_discount_value') || (Number(form.max_promo_discount_value) > 0 ? formatCop(form.max_promo_discount_value) : 'Tope por viaje')}
                      sx={fieldSx}
                        />
                    </Grid>
                ) : null}
                <Grid item xs={12} sm={isPct ? 4 : 6}>
                        <TextField
                            fullWidth
                    label="Pedido mínimo (COP)"
                    value={form.min_order}
                    onChange={(e) => set('min_order')(digits(e.target.value, 9))}
                    inputProps={{ inputMode: 'numeric' }}
                    error={!!err('min_order')}
                    helperText={err('min_order') || (Number(form.min_order) > 0 ? formatCop(form.min_order) : 'Vacío = sin mínimo')}
                    sx={fieldSx}
                        />
                    </Grid>
              </Grid>
            </C2TableCard>

            <C2TableCard>
              <C2SectionHeading title="Vigencia y límites" subtitle="Cada usuario puede usar el código una sola vez." />
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    disablePast
                      label="Fecha de fin"
                      value={form.promo_validity ? dayjs(form.promo_validity) : null}
                      onChange={(v) => set('promo_validity')(v && v.isValid() ? v.valueOf() : null)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!err('promo_validity'),
                          helperText: err('promo_validity') || (form.promo_validity ? `Vence ${moment(form.promo_validity).fromNow()}` : ' '),
                          sx: fieldSx,
                        },
                      }}
                                />
                            </LocalizationProvider>
                    </Grid>
                <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                    label="Límite de usos"
                    value={form.promo_usage_limit}
                    onChange={(e) => set('promo_usage_limit')(digits(e.target.value, 7))}
                    inputProps={{ inputMode: 'numeric' }}
                    error={!!err('promo_usage_limit')}
                    helperText={err('promo_usage_limit') || (id ? `Vacío = sin límite · ${usedCount} usos registrados` : 'Vacío = sin límite')}
                    sx={fieldSx}
                        />
                    </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 1.5, borderRadius: '12px', border: `1px solid ${C2.border}`, backgroundColor: C2.bg }}>
                    <Box>
                      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.9rem', color: C2.sidebar }}>Visible en la app</Typography>
                      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: C2.muted }}>Si la apagas, el código deja de aparecer en la lista de cupones.</Typography>
                    </Box>
                    <C2Switch checked={!!form.promo_show} onChange={(e) => set('promo_show')(e.target.checked)} />
                  </Box>
                </Grid>
              </Grid>
            </C2TableCard>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                position: { md: 'sticky' },
                top: { md: 88 },
                borderRadius: '16px',
                p: { xs: 2, sm: 2.5 },
                color: C2.white,
                background: `linear-gradient(145deg, ${C2.deep} 0%, ${C2.sidebar} 100%)`,
                boxShadow: '0 12px 28px rgba(26,15,59,0.22)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${C2.violet}, ${C2.bright})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LocalOfferOutlinedIcon sx={{ color: C2.white, fontSize: 22 }} />
                </Box>
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.95rem' }}>Vista previa</Typography>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  borderRadius: '14px',
                  p: 2,
                  mb: 2,
                  background: `linear-gradient(120deg, ${C2.violet} 0%, ${C2.bright} 100%)`,
                  boxShadow: '0 10px 24px rgba(100,17,237,0.35)',
                  '&::before, &::after': { content: '""', position: 'absolute', top: '50%', width: 22, height: 22, mt: '-11px', borderRadius: '50%', backgroundColor: C2.sidebar },
                  '&::before': { left: -11 },
                  '&::after': { right: -11 },
                }}
              >
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 900, fontSize: '1.7rem', lineHeight: 1.1 }}>{discountText}</Typography>
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.95rem', mt: 0.5 }}>{form.promo_name || 'Nombre de la promoción'}</Typography>
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', opacity: 0.85 }}>{form.promo_description || 'Descripción corta del beneficio'}</Typography>
                <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px dashed rgba(255,255,255,0.45)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontFamily: C2_FONT, fontWeight: 900, letterSpacing: '0.14em', fontSize: '1.05rem' }}>{code || 'CÓDIGO'}</Typography>
                  <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', opacity: 0.85, textAlign: 'right' }}>
                    {form.promo_validity ? `Hasta ${moment(form.promo_validity).format('ll')}` : 'Sin fecha'}
                  </Typography>
                </Box>
              </Box>

              <PreviewRow label="Tope por viaje" value={isPct ? (promoPreview.max_promo_discount_value ? formatCop(promoPreview.max_promo_discount_value) : '—') : 'No aplica'} />
              <PreviewRow label="Pedido mínimo" value={promoPreview.min_order ? formatCop(promoPreview.min_order) : 'Sin mínimo'} />
              <PreviewRow label="Usos" value={promoPreview.promo_usage_limit ? `${usedCount} / ${promoPreview.promo_usage_limit}` : `${usedCount} · sin límite`} />

              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '12px', backgroundColor: 'rgba(235,231,251,0.08)' }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Simular viaje de (COP)"
                  value={sampleFare}
                  placeholder={String(fare)}
                  onChange={(e) => setSampleFare(digits(e.target.value, 9))}
                  inputProps={{ inputMode: 'numeric' }}
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': { borderRadius: '10px', color: C2.white, fontFamily: C2_FONT, '& fieldset': { borderColor: 'rgba(235,231,251,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#C4B5FD' } },
                    '& .MuiInputLabel-root': { color: 'rgba(251,250,254,0.7)', fontFamily: C2_FONT },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C4B5FD' },
                  }}
                />
                {eligible ? (
                  <>
                    <PreviewRow label="Valor del viaje" value={formatCop(fare)} />
                    <PreviewRow label="Descuento" value={`- ${formatCop(discount)}`} accent="#86EFAC" />
                    <PreviewRow label="Paga el usuario" value={formatCop(fare - discount)} strong />
                  </>
                ) : (
                  <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.82rem', color: '#FCD34D' }}>
                    Con {formatCop(fare)} no aplica: el pedido mínimo es {formatCop(promoPreview.min_order)}.
                            </Typography>
                )}
              </Box>

              {touched && hasErrors ? (
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: '#FCA5A5', mt: 1.5 }}>Revisa los campos marcados en rojo.</Typography>
              ) : null}

              <C2PrimaryButton
                fullWidth
                startIcon={<SaveOutlinedIcon />}
                disabled={saving}
                onClick={handleSave}
                sx={{ mt: 2, py: 1.25, '&.Mui-disabled': { background: 'rgba(235,231,251,0.16)', color: 'rgba(251,250,254,0.5)', boxShadow: 'none' } }}
              >
                {saving ? 'Guardando…' : id ? 'Guardar cambios' : 'Crear promoción'}
              </C2PrimaryButton>
            </Box>
                    </Grid>
                </Grid>
      </ThemeProvider>
    </Box>
  );
}
